import React, { useCallback, useEffect, useState } from "react";
import Layout from "../components/Layout";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { FileText, Download, Sparkles, ShieldCheck, AlertTriangle, XCircle } from "lucide-react";
import { toast } from "sonner";

const STATUS_STYLES = {
  PASS: { c: "text-emerald-400 border-emerald-500/40 bg-emerald-500/10", icon: ShieldCheck },
  PARTIAL: { c: "text-amber-400 border-amber-500/40 bg-amber-500/10", icon: AlertTriangle },
  FAIL: { c: "text-red-400 border-red-500/40 bg-red-500/10", icon: XCircle },
};

export default function Compliance() {
  const [snap, setSnap] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/compliance/snapshot");
      setSnap(data);
    } catch (err) {
      console.error("compliance load failed", err);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const downloadPdf = async () => {
    setBusy(true);
    try {
      const res = await api.post("/compliance/snapshot.pdf", {}, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `rustwall-soc2-${new Date().toISOString().slice(0, 10)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("PDF generated");
    } catch (err) {
      toast.error("PDF generation failed");
    } finally {
      setBusy(false);
    }
  };

  if (!snap) {
    return <Layout title="Compliance" subtitle="SOC2 SNAPSHOT"><div className="text-zinc-500 font-mono text-sm">loading...</div></Layout>;
  }

  const ratio = (snap.passing / snap.total) * 100;

  return (
    <Layout title="Compliance Snapshot" subtitle="SOC2 / ISO27001">
      <div className="grid grid-cols-12 gap-4">
        {/* Hero */}
        <div className="col-span-12 lg:col-span-7 border border-zinc-800 rounded-sm bg-zinc-900/60 p-6 beam-border fade-up">
          <div className="flex items-start justify-between gap-6">
            <div>
              <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-cyan-400 mb-2">// AI-NARRATED ATTESTATION</div>
              <h2 className="font-display text-3xl font-bold tracking-tight">
                {snap.passing}/{snap.total} controls passing
              </h2>
              <p className="text-zinc-400 mt-3 max-w-lg text-sm">
                Generate a portable SOC2-style snapshot covering your firewall policies, RBAC posture,
                multi-cloud connector health, audit completeness, and threat-response automation —
                narrated by Claude Sonnet 4.5.
              </p>
            </div>
            <div className="shrink-0 w-32 h-32 relative">
              <svg viewBox="0 0 36 36" className="w-32 h-32 -rotate-90">
                <circle cx="18" cy="18" r="16" fill="none" stroke="#27272a" strokeWidth="2" />
                <circle cx="18" cy="18" r="16" fill="none" stroke="#06b6d4" strokeWidth="2"
                  strokeDasharray={`${ratio}, 100`} strokeLinecap="round" />
              </svg>
              <div className="absolute inset-0 grid place-items-center">
                <div className="text-center">
                  <div className="font-display text-3xl font-bold text-cyan-400 tabular-nums">{Math.round(ratio)}%</div>
                  <div className="text-[9px] font-mono uppercase tracking-widest text-zinc-500">posture</div>
                </div>
              </div>
            </div>
          </div>
          <div className="flex gap-2 mt-6">
            <Button data-testid="download-pdf-btn" onClick={downloadPdf} disabled={busy} className="bg-cyan-500 hover:bg-cyan-400 text-zinc-950 rounded-sm font-semibold gap-2">
              {busy ? <><Sparkles className="w-4 h-4 animate-pulse" />GENERATING...</> : <><Download className="w-4 h-4" />DOWNLOAD PDF</>}
            </Button>
            <Button data-testid="refresh-snapshot-btn" onClick={load} variant="outline" className="bg-zinc-900 border-zinc-700 hover:bg-zinc-800 rounded-sm gap-2">
              <FileText className="w-4 h-4" />REFRESH
            </Button>
          </div>
        </div>

        {/* KPI grid */}
        <div className="col-span-12 lg:col-span-5 grid grid-cols-2 gap-3 fade-up">
          <KPI label="OPERATORS" v={snap.metrics.users.total} sub={`${snap.metrics.users.admins} admin · ${snap.metrics.users.google_sso} SSO`} />
          <KPI label="ACTIVE POLICIES" v={`${snap.metrics.policies.active}/${snap.metrics.policies.total}`} sub="rules in force" />
          <KPI label="THREAT EVENTS" v={snap.metrics.threats.total} sub={`${snap.metrics.threats.blocked} blocked`} />
          <KPI label="CRITICAL" v={snap.metrics.threats.critical} sub="severity escalations" />
          <KPI label="AUTO-HEALED" v={snap.metrics.healing.total} sub="incidents remediated" />
          <KPI label="AUDIT ENTRIES" v={snap.metrics.audit.total} sub="immutable trail" />
          <KPI label="CONNECTORS HEALTHY" v={`${snap.metrics.connectors_healthy}/${snap.metrics.connectors_total}`} sub="multi-cloud" />
          <KPI label="TLS PROXY ROUTES" v={`${snap.metrics.proxy.tls}/${snap.metrics.proxy.total}`} sub="encrypted" />
        </div>

        {/* Controls */}
        <div className="col-span-12 border border-zinc-800 rounded-sm bg-zinc-900/40 fade-up">
          <div className="px-4 py-3 border-b border-zinc-800 text-[10px] font-mono uppercase tracking-[0.25em] text-zinc-400">
            CONTROL ASSESSMENT
          </div>
          <div className="divide-y divide-zinc-800/70" data-testid="controls-list">
            {snap.controls.map((c) => {
              const s = STATUS_STYLES[c.status] || STATUS_STYLES.FAIL;
              const Icon = s.icon;
              return (
                <div key={c.id} className="grid grid-cols-12 gap-3 items-center px-4 py-3" data-testid={`control-${c.id}`}>
                  <div className="col-span-2 font-mono text-cyan-400 text-sm">{c.id}</div>
                  <div className="col-span-6 text-sm text-zinc-200">{c.name}</div>
                  <div className="col-span-2 font-mono text-xs text-zinc-300 tabular-nums">{c.score}/100</div>
                  <div className="col-span-2 text-right">
                    <span className={`inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 border rounded-sm ${s.c}`}>
                      <Icon className="w-3 h-3" /> {c.status}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </Layout>
  );
}

function KPI({ label, v, sub }) {
  return (
    <div className="border border-zinc-800 rounded-sm bg-zinc-900/60 p-3" data-testid={`kpi-${label.toLowerCase().replace(/ /g, "-")}`}>
      <div className="text-[9px] font-mono uppercase tracking-[0.25em] text-zinc-500">{label}</div>
      <div className="font-display text-2xl font-bold tabular-nums mt-1">{v}</div>
      <div className="text-[10px] font-mono text-zinc-500 mt-0.5">{sub}</div>
    </div>
  );
}
